import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@bliks-io/ui';
import { useSearchHubspotCompanies } from '@/pages/admin/api/queries/useAccounts';
import { useAddHubspotCompany } from '@/pages/admin/api/mutations/useAccounts';
import { CompanyHitLine, CrmSearch } from './CrmSearch';

interface AddAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (hubspotCompanyId?: string) => void;
  isCreating?: boolean;
}

/**
 * Creates a customer, normally by naming the HubSpot company they are.
 *
 * Creating one WITHOUT a company stays possible on purpose: a customer can be
 * agreed before anyone has looked up their CRM record, and forcing a company
 * here would push somebody into picking the nearest-looking one — the mistake
 * this picker exists to prevent.
 */
export function AddAccountDialog({
  open,
  onOpenChange,
  onCreate,
  isCreating
}: AddAccountDialogProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const { data, isLoading } = useSearchHubspotCompanies(term);
  const addCompany = useAddHubspotCompany();

  // Create the CRM record first, then the customer around it. The other order
  // would leave an account claiming a company that was never created.
  const createCompanyThenAccount = () => {
    addCompany.mutate({ name: term.trim() }, { onSuccess: ({ companyId }) => onCreate(companyId) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('accountAdmin.addAccount')}</DialogTitle>
          <DialogDescription>{t('accountAdmin.addAccountHint')}</DialogDescription>
        </DialogHeader>

        <CrmSearch
          autoFocus
          term={term}
          onTermChange={setTerm}
          placeholder={t('accountAdmin.searchCompanyPlaceholder')}
          isLoading={isLoading}
          hits={data?.companies ?? []}
          keyOf={(hit) => hit.companyId}
          busy={isCreating || addCompany.isPending}
          // A company that is already a customer cannot become a second one.
          isDisabled={(hit) => hit.linkedAccount !== null}
          renderHit={(hit) => (
            <CompanyHitLine
              hit={hit}
              badge={
                hit.linkedAccount !== null && (
                  <Badge variant="outline" className="shrink-0">
                    {t('accountAdmin.companyTaken')}
                  </Badge>
                )
              }
            />
          )}
          onPick={(hit) => onCreate(hit.companyId)}
          minText={t('accountAdmin.searchCompanyMin')}
          emptyText={t('accountAdmin.searchCompanyEmpty')}
          // Offered only where nothing was found — creating a duplicate is the risk.
          whenEmpty={
            <Button
              variant="outline"
              size="small"
              className="mt-3"
              disabled={isCreating || addCompany.isPending}
              onClick={createCompanyThenAccount}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('accountAdmin.createCompany', { name: term.trim() })}
            </Button>
          }
        />

        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={() => onCreate(undefined)} disabled={isCreating}>
            {t('accountAdmin.createWithoutCompany')}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
